import { create } from 'zustand';
import type { AgentSessionDto, AgentMessageDto, CreateSessionRequest, SendMessageRequest } from '../types/session';
import { sessionService } from '../services/sessionService';

/**
 * Session store state interface.
 */
interface SessionState {
  // State
  currentSession: AgentSessionDto | null;
  sessions: AgentSessionDto[];
  sessionsTotalCount: number;
  messages: Map<string, AgentMessageDto[]>;
  /** Page-level loading — only true during initial session fetch (controls skeleton). */
  loading: boolean;
  /** Background loading — messages, mutations. Does NOT trigger page skeleton. */
  busy: boolean;
  error: string | null;

  // Actions
  setCurrentSession: (session: AgentSessionDto | null) => void;
  loadSessions: (filters?: any) => Promise<void>;
  loadSession: (id: string) => Promise<void>;
  loadMessages: (sessionId: string, pageNumber?: number, pageSize?: number) => Promise<void>;
  createSession: (data: CreateSessionRequest) => Promise<AgentSessionDto>;
  updateSession: (id: string, data: { sessionName?: string }) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  pauseSession: (id: string) => Promise<void>;
  resumeSession: (id: string) => Promise<void>;
  completeSession: (id: string) => Promise<AgentSessionDto>;
  sendMessage: (sessionId: string, data: SendMessageRequest) => Promise<AgentMessageDto>;
  addMessage: (sessionId: string, message: AgentMessageDto) => void;
  clearError: () => void;
  clearMessages: (sessionId: string) => void;
}

/**
 * Session store using Zustand.
 */
export const useSessionStore = create<SessionState>((set, get) => ({
  // Initial state
  currentSession: null,
  sessions: [],
  sessionsTotalCount: 0,
  messages: new Map(),
  loading: false,
  busy: false,
  error: null,

  // Set current session
  setCurrentSession: (session) => {
    set({ currentSession: session });
  },

  // Load sessions — list page uses grid's own loading state, so use `busy` here
  loadSessions: async (filters) => {
    set({ busy: true, error: null });
    try {
      const result = await sessionService.getSessions(filters);
      set({ sessions: result.items, sessionsTotalCount: result.totalCount, busy: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to load sessions', busy: false });
    }
  },

  // Load single session — single atomic `set` avoids intermediate "not found" flash
  loadSession: async (id) => {
    const prev = get().currentSession;
    set({
      // Keep old session visible if same ID (avoids flash); clear if navigating away
      currentSession: prev?.id === id ? prev : null,
      loading: true,
      error: null,
    });
    try {
      const session = await sessionService.getSession(id);
      set({ currentSession: session, loading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to load session', loading: false });
    }
  },

  // Load messages for a session — uses `busy` (not `loading`) to avoid page-level skeleton flicker
  loadMessages: async (sessionId, pageNumber, pageSize) => {
    set({ busy: true });
    try {
      const fetched = await sessionService.getMessages(sessionId, pageNumber, pageSize);
      const newMessages = new Map(get().messages);

      if (pageNumber && pageNumber > 1) {
        // Paginating: append older messages to the front of existing list
        const currentMessages = get().messages.get(sessionId) || [];
        newMessages.set(sessionId, [...fetched, ...currentMessages]);
      } else {
        // Initial load or refresh: replace to avoid duplicates
        newMessages.set(sessionId, fetched);
      }

      set({ messages: newMessages, busy: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to load messages', busy: false });
    }
  },

  // Create session
  createSession: async (data) => {
    set({ busy: true, error: null });
    try {
      const session = await sessionService.createSession(data);
      set((state) => ({
        sessions: [session, ...state.sessions],
        currentSession: session,
        busy: false,
      }));
      return session;
    } catch (error: any) {
      set({ error: error.message || 'Failed to create session', busy: false });
      throw error;
    }
  },

  // Update session
  updateSession: async (id, data) => {
    set({ busy: true, error: null });
    try {
      const updated = await sessionService.updateSession(id, data);
      set((state) => ({
        sessions: state.sessions.map((s) => (s.id === id ? updated : s)),
        currentSession: state.currentSession?.id === id ? updated : state.currentSession,
        busy: false,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to update session', busy: false });
      throw error;
    }
  },

  // Delete session
  deleteSession: async (id) => {
    set({ busy: true, error: null });
    try {
      await sessionService.deleteSession(id);
      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== id),
        currentSession: state.currentSession?.id === id ? null : state.currentSession,
        busy: false,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to delete session', busy: false });
      throw error;
    }
  },

  // Pause session
  pauseSession: async (id) => {
    set({ busy: true, error: null });
    try {
      const updated = await sessionService.pauseSession(id);
      set((state) => ({
        sessions: state.sessions.map((s) => (s.id === id ? updated : s)),
        currentSession: state.currentSession?.id === id ? updated : state.currentSession,
        busy: false,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to pause session', busy: false });
      throw error;
    }
  },

  // Resume session
  resumeSession: async (id) => {
    set({ busy: true, error: null });
    try {
      const updated = await sessionService.resumeSession(id);
      set((state) => ({
        sessions: state.sessions.map((s) => (s.id === id ? updated : s)),
        currentSession: state.currentSession?.id === id ? updated : state.currentSession,
        busy: false,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to resume session', busy: false });
      throw error;
    }
  },

  // Complete session (explicit end-of-session signal; also queues memory extraction)
  completeSession: async (id) => {
    set({ busy: true, error: null });
    try {
      const updated = await sessionService.completeSession(id);
      set((state) => ({
        sessions: state.sessions.map((s) => (s.id === id ? updated : s)),
        currentSession: state.currentSession?.id === id ? updated : state.currentSession,
        busy: false,
      }));
      return updated;
    } catch (error: any) {
      set({ error: error.message || 'Failed to complete session', busy: false });
      throw error;
    }
  },

  // Send message
  sendMessage: async (sessionId, data) => {
    set({ busy: true, error: null });
    try {
      const message = await sessionService.sendMessage(sessionId, data);
      const currentMessages = get().messages.get(sessionId) || [];
      // Avoid duplicate if message with same ID already exists
      if (currentMessages.some((m) => m.id === message.id)) {
        set({ busy: false });
        return message;
      }
      const newMessages = new Map(get().messages);
      newMessages.set(sessionId, [...currentMessages, message]);
      set({ messages: newMessages, busy: false });
      return message;
    } catch (error: any) {
      set({ error: error.message || 'Failed to send message', busy: false });
      throw error;
    }
  },

  // Add message (for real-time updates)
  addMessage: (sessionId, message) => {
    const currentMessages = get().messages.get(sessionId) || [];
    // Avoid duplicate if message with same ID already exists
    if (currentMessages.some((m) => m.id === message.id)) {
      return;
    }
    const newMessages = new Map(get().messages);
    newMessages.set(sessionId, [...currentMessages, message]);
    set({ messages: newMessages });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Clear messages for a session
  clearMessages: (sessionId) => {
    const newMessages = new Map(get().messages);
    newMessages.delete(sessionId);
    set({ messages: newMessages });
  },
}));
